import { invoke } from '@forge/bridge'
import { Button, Textarea } from '@heroui/react'
import { useState } from 'react'
import { api } from '../../lib/api'
import { getRadarData, useRadarNav } from '../../lib/state'

export const DataManagement = () => {
  const { radarId } = useRadarNav()

  const [importError, setImportError] = useState<string | null>(null)
  const [importSuccess, setImportSuccess] = useState(false)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [jsonInput, setJsonInput] = useState('')
  const [showExportWarning, setShowExportWarning] = useState(false)
  const [pendingImportJson, setPendingImportJson] = useState<string | null>(
    null
  )
  const [showExportJson, setShowExportJson] = useState(false)
  const [exportJson, setExportJson] = useState<string>('')

  const handleFullImport = async (jsonContent: string) => {
    // Show export warning modal before proceeding
    setPendingImportJson(jsonContent)
    setShowExportWarning(true)
  }

  // Confirmed: export backup before import
  const handleConfirmExportAndImport = async () => {
    setShowExportWarning(false)
    if (!pendingImportJson) return
    try {
      // Export full backup as a precaution
      const exportResult = await api.exportAllRadars()
      const dataStr = JSON.stringify(exportResult, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      const url = URL.createObjectURL(dataBlob)
      const now = new Date()
      const date = now.toISOString().slice(0, 19).replace('T', '_')
      const link = document.createElement('a')
      link.href = url
      link.download = `TechRadar_Backup_${date}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (_err) {
      // If export fails, still proceed with import but show warning
      setImportError(
        'Warning: Failed to export backup before import. Proceeding with import.'
      )
    }
    // Now proceed with import
    try {
      const parsed = JSON.parse(pendingImportJson)
      if (
        typeof parsed !== 'object' ||
        typeof parsed.schemaVersion !== 'number' ||
        !Array.isArray(parsed.radars) ||
        typeof parsed.data !== 'object'
      ) {
        throw new Error(
          'Invalid backup format: must be a full radar backup exported from this app.'
        )
      }
      const resp = await invoke<{ success: boolean; error?: string }>(
        'importAllRadars',
        parsed
      )
      if (!resp.success) {
        throw new Error(resp.error || 'Unknown error during import')
      }
      setImportSuccess(true)
      setTimeout(() => setImportSuccess(false), 5000)
      setImportModalOpen(false)
      setJsonInput('')
      await getRadarData(radarId)
    } catch (err) {
      setImportError(
        err instanceof Error ? err.message : 'Failed to import backup'
      )
    }
    setPendingImportJson(null)
  }

  const handleCancelExportAndImport = () => {
    setShowExportWarning(false)
    setPendingImportJson(null)
  }

  const handleFullExport = async () => {
    try {
      // Download as before
      const exportResult = await api.exportAllRadars()
      const dataStr = JSON.stringify(exportResult, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      const url = URL.createObjectURL(dataBlob)
      const now = new Date()
      const date = now.toISOString().slice(0, 19).replace('T', '_')
      const link = document.createElement('a')
      link.href = url
      link.download = `TechRadar_Backup_${date}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      // Also get JSON string for inline display
      const jsonStr = JSON.stringify(exportResult, null, 2)
      setExportJson(jsonStr)
      setShowExportJson(true)
    } catch (err) {
      setImportError(
        err instanceof Error ? err.message : 'Failed to export full backup'
      )
    }
  }

  return (
    <div className="space-y-6" data-testid="data-management">
      <div>
        <h2
          className="text-xl font-semibold text-[var(--ds-text)] mb-3"
          data-testid="data-management-title"
        >
          Backup & Restore
        </h2>
        <div
          className="flex items-center gap-2"
          data-testid="data-management-actions"
        >
          <Button
            variant="bordered"
            className="text-[var(--ds-text)] rounded-[3px] h-8 px-3 border border-[var(--ds-border)] bg-transparent hover:bg-transparent"
            size="sm"
            disableRipple
            onPress={handleFullExport}
            data-testid="export-backup-json-button"
            aria-label="Export full backup as JSON file"
          >
            Export Full Backup
          </Button>
          <Button
            variant="bordered"
            className="text-[var(--ds-text)] rounded-[3px] h-8 px-3 border border-[var(--ds-border)] bg-transparent hover:bg-transparent"
            size="sm"
            disableRipple
            onPress={() => setImportModalOpen(!importModalOpen)}
            data-testid="import-backup-json-button"
            aria-expanded={importModalOpen}
            aria-controls="import-inline-section"
          >
            {importModalOpen ? 'Cancel Import' : 'Import Full Backup'}
          </Button>
        </div>
        {importSuccess && (
          <div
            className="mt-2 text-sm text-[var(--colors-success)] bg-[var(--ds-background-success)] p-2 rounded-sm"
            data-testid="import-success-message"
            role="alert"
            aria-live="polite"
          >
            Data imported successfully! All radars and items have been restored
            from backup.
          </div>
        )}

        {/* Inline Import JSON section */}
        {importModalOpen && (
          <div
            className="mt-6 border border-[var(--ds-border)] rounded-[3px] overflow-hidden bg-[var(--ds-surface)]"
            id="import-inline-section"
            data-testid="import-inline-section"
            aria-labelledby="import-inline-title"
          >
            <div className="border-b border-[var(--ds-border)] p-4">
              <h3
                id="import-inline-title"
                className="text-lg font-medium text-[var(--ds-text)]"
                data-testid="import-inline-title"
              >
                Import JSON Data
              </h3>
            </div>
            <div className="p-4">
              <div className="space-y-4">
                <div>
                  <div
                    className="text-sm text-[var(--ds-text-subtle)] mb-2"
                    id="paste-json-label"
                    data-testid="paste-json-label"
                  >
                    Paste your exported TechRadar backup JSON here
                  </div>
                  <Textarea
                    value={jsonInput}
                    onChange={e => setJsonInput(e.target.value)}
                    placeholder='{"schemaVersion": 1, "radars": [...], "data": {...}}'
                    rows={10}
                    data-testid="json-input-textarea"
                    aria-labelledby="paste-json-label"
                    aria-describedby="json-input-error"
                    classNames={{
                      input:
                        'text-sm font-mono group-data-[has-value=true]:text-[var(--ds-text)]',
                      inputWrapper:
                        'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-(--ds-surface)! rounded-[3px] data-[focused=true]:border-(--ds-border-focused)!'
                    }}
                  />
                </div>
                <div>
                  <div
                    className="text-sm text-[var(--ds-text-subtle)] mb-2"
                    id="upload-json-label"
                    data-testid="upload-json-label"
                  >
                    Or select a backup JSON file to import
                  </div>
                  <input
                    type="file"
                    accept=".json"
                    onChange={async e => {
                      const file = e.target.files?.[0]
                      if (!file) return
                      try {
                        const text = await file.text()
                        setJsonInput(text)
                      } catch (err) {
                        setImportError(
                          err instanceof Error
                            ? err.message
                            : 'Failed to read file'
                        )
                      }
                    }}
                    className="block w-full text-sm text-[var(--ds-text-subtle)] file:mr-4 file:py-2 file:px-4 file:rounded-[3px] file:border file:border-[var(--ds-border)] file:text-sm file:bg-transparent file:text-[var(--ds-text)]"
                    data-testid="json-file-input"
                    aria-labelledby="upload-json-label"
                    aria-describedby="json-input-error"
                  />
                </div>

                {importError && (
                  <div
                    id="json-input-error"
                    className="mt-2 text-sm text-[var(--ds-text-danger)] bg-[var(--ds-background-danger)] p-2 rounded-sm border border-[var(--ds-border-danger)] font-semibold"
                    data-testid="import-error-message"
                    role="alert"
                    aria-live="assertive"
                    style={{
                      color: 'var(--ds-text-danger, #ff5555)',
                      background: 'var(--ds-background-danger, #220000)'
                    }}
                  >
                    {importError}
                    <button
                      className="ml-4 px-2 py-0.5 rounded bg-[var(--ds-background-danger)] text-[var(--ds-text-danger)] border border-[var(--ds-border-danger)] hover:bg-[var(--ds-background-danger-hover)]"
                      onClick={() => setImportError(null)}
                      aria-label="Dismiss error message"
                      type="button"
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="border-t border-[var(--ds-border)] p-4 flex gap-2 justify-end">
              <Button
                variant="solid"
                color="primary"
                size="sm"
                onPress={async () => {
                  if (!jsonInput.trim()) {
                    setImportError('Please paste JSON data')
                    return
                  }
                  await handleFullImport(jsonInput)
                }}
                data-testid="confirm-import-button"
              >
                Import
              </Button>
              <Button
                variant="bordered"
                className="text-[var(--ds-text)] rounded-[3px] h-8 px-3 border border-[var(--ds-border)] bg-transparent hover:bg-transparent"
                size="sm"
                onPress={() => {
                  setImportModalOpen(false)
                  setJsonInput('')
                  setImportError(null)
                }}
                data-testid="cancel-import-inline"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
        {/* Export Warning Dialog */}
        {showExportWarning && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-[var(--ds-surface)] border border-[var(--ds-border)] rounded-[8px] shadow-xl w-full max-w-md p-8">
              <h3 className="text-lg font-medium text-[var(--ds-text)] mb-4">
                Export Current Data Before Import
              </h3>
              <div className="mb-4 text-[var(--ds-text-subtle)]">
                As a precaution, a full backup of your current radar data will
                be exported before importing the new backup. This ensures you
                can always restore your previous state if needed.
              </div>
              <div className="text-sm text-[var(--ds-text-subtle)] mb-8">
                Proceeding will trigger a download of your current data, then
                complete the import.
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="bordered"
                  className="text-[var(--ds-text)] rounded-[3px] h-8 px-3 border border-[var(--ds-border)] bg-transparent hover:bg-transparent"
                  size="sm"
                  disableRipple
                  onPress={handleCancelExportAndImport}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  variant="solid"
                  color="primary"
                  className="rounded-[3px] h-8 px-3"
                  size="sm"
                  disableRipple
                  onPress={handleConfirmExportAndImport}
                  type="button"
                  autoFocus
                >
                  Export & Continue Import
                </Button>
              </div>
            </div>
          </div>
        )}
        {/* Inline Export JSON section */}
        {showExportJson && (
          <div
            className="mt-6 border border-[var(--ds-border)] rounded-[3px] overflow-hidden bg-[var(--ds-surface)]"
            data-testid="export-inline-section"
          >
            <div className="border-b border-[var(--ds-border)] p-4 flex justify-between items-center">
              <h3
                className="text-lg font-medium text-[var(--ds-text)]"
                data-testid="export-inline-title"
              >
                Exported JSON Data
              </h3>
              <Button
                variant="bordered"
                size="sm"
                className="ml-2 text-[var(--ds-text)] rounded-[3px] h-8 px-3 border border-[var(--ds-border)] bg-transparent hover:bg-transparent"
                onPress={() => setShowExportJson(false)}
                data-testid="close-export-inline"
                aria-label="Close export JSON display"
              >
                Close
              </Button>
            </div>
            <div className="p-4">
              <Textarea
                value={exportJson}
                readOnly
                rows={12}
                data-testid="export-json-textarea"
                classNames={{
                  input:
                    'text-sm font-mono group-data-[has-value=true]:text-[var(--ds-text)]',
                  inputWrapper:
                    'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-(--ds-surface)! rounded-[3px] data-[focused=true]:border-(--ds-border-focused)!'
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
